import React, { useState } from 'react';
import Loading from './Loading';
import ErrorMessage from './ErrorMessage';
import '../../styles/components.css';

const DataTable = ({
  columns = [],
  data = [],
  loading = false,
  error = null,
  onEdit = null,
  onDelete = null,
  onView = null,
  actions = true,
  emptyMessage = 'No hay datos disponibles',
  itemsPerPage = null,
  hideEdit = false,
  hideDelete = false
}) => {
  const [sortColumn, setSortColumn] = useState(null);
  const [sortDirection, setSortDirection] = useState('asc');
  const [currentPage, setCurrentPage] = useState(1);

  const handleSort = (columnKey) => {
    if (sortColumn === columnKey) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(columnKey);
      setSortDirection('asc');
    }
  };

  const sortedData = React.useMemo(() => {
    if (!sortColumn) return data;

    return [...data].sort((a, b) => {
      const aValue = a[sortColumn];
      const bValue = b[sortColumn];

      if (aValue === bValue) return 0;
      
      const comparison = aValue > bValue ? 1 : -1;
      return sortDirection === 'asc' ? comparison : -comparison;
    });
  }, [data, sortColumn, sortDirection]);

  // Paginación
  const paginatedData = React.useMemo(() => {
    if (!itemsPerPage) return sortedData;
    
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return sortedData.slice(startIndex, endIndex);
  }, [sortedData, currentPage, itemsPerPage]);

  const totalPages = itemsPerPage ? Math.ceil(sortedData.length / itemsPerPage) : 1;

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  if (loading) {
    return <Loading />;
  }

  if (error) {
    return <ErrorMessage message={error} type="error" />;
  }

  if (!data || data.length === 0) {
    return (
      <div className="table-empty">
        <p>{emptyMessage}</p>
      </div>
    );
  }

  return (
    <div className="data-table-container">
      <div className="table-responsive">
        <table className="data-table">
          <thead>
            <tr>
              {columns.map((column) => (
                <th 
                  key={column.key}
                  onClick={() => column.sortable && handleSort(column.key)}
                  className={column.sortable ? 'sortable' : ''}
                >
                  <div className="th-content">
                    <span>{column.label}</span>
                    {column.sortable && sortColumn === column.key && (
                      <span className="sort-icon">
                        {sortDirection === 'asc' ? '↑' : '↓'}
                      </span>
                    )}
                  </div>
                </th>
              ))}
              {actions && <th className="actions-column">Acciones</th>}
            </tr>
          </thead>
          <tbody>
            {paginatedData.map((row, index) => (
              <tr key={row.id || index}>
                {columns.map((column) => (
                  <td key={column.key}>
                    {column.render 
                      ? column.render(row[column.key], row)
                      : row[column.key]
                    }
                  </td>
                ))}
                {actions && (
                  <td className="actions-cell">
                    <div className="action-buttons">
                      {onView && (
                        <button
                          className="btn-action btn-view"
                          onClick={() => onView(row)}
                          title="Ver detalles"
                        >
                          👁️
                        </button>
                      )}
                      {onEdit && !hideEdit && (
                        <button
                          className="btn-action btn-edit"
                          onClick={() => onEdit(row)}
                          title="Editar"
                        >
                          ✏️
                        </button>
                      )}
                      {onDelete && !hideDelete && (
                        <button
                          className="btn-action btn-delete"
                          onClick={() => onDelete(row)}
                          title="Eliminar"
                        >
                          🗑️
                        </button>
                      )}
                    </div>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      
      {itemsPerPage && totalPages > 1 && (
        <div className="pagination">
          <button
            className="pagination-btn"
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
          >
            ← Anterior
          </button>
          
          <div className="pagination-info">
            <span>Página {currentPage} de {totalPages}</span>
            <span className="pagination-total">
              Total de registros: {sortedData.length}
            </span>
          </div>
          
          <button
            className="pagination-btn"
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
          >
            Siguiente →
          </button>
        </div>
      )}
    </div>
  );
};

export default DataTable;

// Made with Bob
